import clsx from "clsx"
import { useState } from "react"

import useSlugExtractor from "@hooks/useSlugExtractor"
import useUser from "@hooks/useUser"

import { findCoveringSubscription, findItemSubscription } from "@utils/catalogSubscriptions"
import { faIcon } from "@utils/faIcon"
import formatCurrency from "@utils/formatCurrency"
import replaceSlugs from "@utils/replaceSlugs"

import { AccessCheck, Skeleton } from "@particles/index"

import { Avatar, Button, Hint, Icon, LinkButton } from "@atoms/index"

import AlertBadge from "@molecules/AlertBadge/AlertBadge"
import EmptyState from "@molecules/EmptyState/EmptyState"
import ValueDisplay from "@molecules/ValueDisplay/ValueDisplay"

import FileCarousel from "@organisms/SidePanel/SidePanelFilePreview/FileCarousel/FileCarousel"
import { TopBar } from "@organisms/index"

import { AppFrame } from "@templates/index"

import {
    useCatalogSubscriptionsQuery,
    useSubscribeMutation,
    useUnsubscribeMutation,
} from "@pages/Settings/SettingsCatalogs/SettingsCatalogs.store"

import { CATALOG_ROUTES } from "@routes/catalogs"
import { PRICEBOOK_ITEMS_ROUTES } from "@routes/pricebook-items"

import { PERMISSION_LEVEL } from "@constants/permissionLevel"

import styles from "./ItemDetails.module.scss"
import { useCatalogItemModifiersQuery, useCatalogItemQuery } from "./ItemDetails.store"
import { CatalogItemDetails as CatalogItemDetailsData, CatalogItemModifierGroupDetails } from "./ItemDetails.types"

const MODIFIER_GROUP_DISPLAY_CAP = 50

function htmlToPlainText(html: string): string {
    // Platform descriptions arrive as store-authored HTML; DOMParser extracts the text without executing
    // anything. Block boundaries become newlines first so paragraphs survive the flattening, and the
    // container renders with pre-line whitespace.
    const htmlWithBreaks = html.replace(/<br\s*\/?>/gi, "\n").replace(/<\/(p|div|li|ul|ol|h[1-6]|table|tr)>/gi, "\n")
    const parsedDocument = new DOMParser().parseFromString(htmlWithBreaks, "text/html")
    const plainText = parsedDocument.body.textContent ?? ""
    return plainText
        .replace(/\n{3,}/g, "\n\n")
        .replace(/[ \t]+\n/g, "\n")
        .trim()
}

function formatMeasurement(value: string | null): string {
    // Measurements are stored with four decimal places; trailing zeros are noise on a details page.
    if (value === null || Number(value) === 0) {
        return ""
    }

    return String(parseFloat(value))
}

function formatDimensions(item: CatalogItemDetailsData): string {
    const dimensionValues = [item.length, item.width, item.height].map(formatMeasurement)

    if (dimensionValues.every((value) => value === "")) {
        return ""
    }

    const unitSuffix = item.dimension_unit_display ? ` ${item.dimension_unit_display}` : ""
    return `${dimensionValues.map((value) => value || "—").join(" × ")}${unitSuffix} (L × W × H)`
}

function formatCategoryNames(categories: CatalogItemDetailsData["categories"]): string {
    const visibleNames = categories
        .slice(0, 3)
        .map((category) => [...category.ancestor_names, category.name].join(" / "))
    const hiddenCount = categories.length - visibleNames.length

    if (hiddenCount > 0) {
        return `${visibleNames.join(", ")} +${hiddenCount} more`
    }

    return visibleNames.join(", ")
}

function formatModifierAdjustment(
    modifier: CatalogItemModifierGroupDetails["modifiers"][number],
    currencyCode: string,
    languageCode: string,
): string {
    if (!modifier.price_adjustment_type || modifier.price_adjustment_value === null) {
        return ""
    }

    if (modifier.price_adjustment_type === "percentage") {
        return ` (+${modifier.price_adjustment_value}%)`
    }

    return ` (+${formatCurrency({ value: Number(modifier.price_adjustment_value), currencyCode, languageCode })})`
}

function ItemGallery(props: { item: CatalogItemDetailsData }) {
    const { item } = props

    const [selectedIndex, setSelectedIndex] = useState(0)
    const selectedAttachment = item.attachments[selectedIndex] ?? item.attachments[0]

    if (item.attachments.length === 0) {
        return (
            <div className={styles.galleryPlaceholder}>
                <Avatar variant="company" size="xl" fallbackName={item.name} />
            </div>
        )
    }

    return (
        <div className={styles.gallery}>
            <div className={styles.galleryMain}>
                <img src={selectedAttachment.signed_url} alt={selectedAttachment.upload_filename} loading="lazy" />
            </div>
            {item.attachments.length > 1 && (
                <FileCarousel
                    files={item.attachments.map(
                        (attachment) =>
                            ({
                                id: String(attachment.id),
                                upload_filename: attachment.upload_filename,
                                upload_mimetype: attachment.upload_mimetype,
                                signed_url: attachment.signed_url,
                                thumbnail_url: attachment.signed_url,
                            }) as Attachment,
                    )}
                    currentIndex={selectedIndex}
                    onSelect={setSelectedIndex}
                />
            )}
        </div>
    )
}

function ItemPrices(props: { item: CatalogItemDetailsData; currencyCode: string; languageCode: string }) {
    const { item, currencyCode, languageCode } = props

    const formattedListPrice = formatCurrency({ value: Number(item.list_price), currencyCode, languageCode })

    // Without a bound customer there is no customer price to compare against, so the
    // list price stands alone and needs no disambiguating label.
    if (item.customer_price === null || item.customer_name === null) {
        return <span className={styles.itemPrice}>{formattedListPrice}</span>
    }

    return (
        <div className={styles.priceComparison}>
            <div className={styles.priceEntry}>
                <span className={styles.priceLabel}>List Price</span>
                <span className={styles.itemPrice}>{formattedListPrice}</span>
            </div>
            <div className={styles.priceEntry}>
                <span className={styles.priceLabel}>Your Price ({item.customer_name})</span>
                <span className={styles.itemPrice}>
                    {formatCurrency({ value: Number(item.customer_price), currencyCode, languageCode })}
                </span>
            </div>
        </div>
    )
}

function ModifierGroupRow(props: {
    group: CatalogItemModifierGroupDetails
    currencyCode: string
    languageCode: string
}) {
    const { group, currencyCode, languageCode } = props

    const [isExpanded, setIsExpanded] = useState(false)

    const modifierCount = group.modifiers.length
    const modifierCountLabel = `${modifierCount} ${modifierCount === 1 ? "option" : "options"}`

    return (
        <div className={styles.modifierGroup}>
            <button
                type="button"
                className={styles.modifierGroupHeader}
                aria-expanded={isExpanded}
                onClick={() => setIsExpanded((previousIsExpanded) => !previousIsExpanded)}
            >
                <Icon
                    size={12}
                    icon={faIcon({ name: "chevron-right", style: "solid" })}
                    className={clsx(styles.chevron, { [styles.chevronExpanded]: isExpanded })}
                />
                <span className={styles.modifierGroupName}>{group.name}</span>
                {group.is_required && <span className={styles.requiredMarker}>(required)</span>}
                <span className={styles.modifierCount}>{modifierCountLabel}</span>
            </button>
            {isExpanded && (
                <ul className={styles.modifierList}>
                    {group.modifiers.map((modifier) => (
                        <li key={modifier.external_id}>
                            {modifier.name}
                            {formatModifierAdjustment(modifier, currencyCode, languageCode)}
                        </li>
                    ))}
                </ul>
            )}
        </div>
    )
}

function ItemOptions(props: { itemId: string }) {
    const { itemId } = props

    const modifiersQuery = useCatalogItemModifiersQuery(itemId)

    const { user } = useUser()
    const currencyCode = user?.service_company?.currency_code ?? "USD"
    const languageCode = user?.service_company?.language_code ?? "en-US"

    if (modifiersQuery.isLoading) {
        return (
            <div className={styles.optionsSkeleton}>
                <Skeleton height={20} width={96} />
                <Skeleton height={36} />
                <Skeleton height={36} />
                <Skeleton height={36} />
            </div>
        )
    }

    // Any failure means the platform is unreachable; hide the section quietly rather than surfacing an error.
    if (modifiersQuery.isError || !modifiersQuery.data) {
        return null
    }

    const groups = modifiersQuery.data.modifier_groups

    // Empty groups are already filtered out server-side, so an empty list means the item has no options.
    if (groups.length === 0) {
        return null
    }

    const visibleGroups = groups.slice(0, MODIFIER_GROUP_DISPLAY_CAP)
    const hiddenGroupCount = groups.length - visibleGroups.length

    return (
        <div className={styles.modifierGroups}>
            <h2 className={styles.sectionTitle}>Options</h2>
            {visibleGroups.map((group) => (
                <ModifierGroupRow
                    key={group.external_id}
                    group={group}
                    currencyCode={currencyCode}
                    languageCode={languageCode}
                />
            ))}
            {hiddenGroupCount > 0 && (
                <span className={styles.hiddenGroupsNote}>
                    Showing {MODIFIER_GROUP_DISPLAY_CAP} of {groups.length} option groups.
                </span>
            )}
        </div>
    )
}

function ItemDetailsContent() {
    const windowData = window as unknown as Record<string, string | undefined>
    const itemId = windowData.CATALOG_ITEM_ID ?? ""
    const catalogId = windowData.CATALOG_ID ?? ""

    const { user } = useUser()
    const currencyCode = user?.service_company?.currency_code ?? "USD"
    const languageCode = user?.service_company?.language_code ?? "en-US"

    const [pricebookItemUpdateTemplate] = useSlugExtractor([PRICEBOOK_ITEMS_ROUTES.UPDATE])

    const itemQuery = useCatalogItemQuery(itemId)
    const subscriptionsQuery = useCatalogSubscriptionsQuery(Number(catalogId))
    const subscribeMutation = useSubscribeMutation()
    const unsubscribeMutation = useUnsubscribeMutation()

    const item = itemQuery.data

    // Coverage is derived off the subscriptions listing rather than baked onto the item detail; the
    // subscribe/unsubscribe mutations invalidate that shared cache, so this view re-derives on its own.
    const subscriptions = subscriptionsQuery.data ?? []
    const coveringSubscription = item
        ? findCoveringSubscription(subscriptions, {
              id: item.id,
              brand: item.brand?.id ?? null,
              categories: item.categories,
          })
        : null
    const itemSubscription = item ? findItemSubscription(subscriptions, item.id) : null
    const isSubscribed = coveringSubscription !== null

    const backDestination = item?.brand
        ? replaceSlugs(CATALOG_ROUTES.BRAND_ITEMS, { id: String(item.brand.id) })
        : catalogId
          ? replaceSlugs(CATALOG_ROUTES.NO_BRAND_ITEMS, { id: catalogId })
          : CATALOG_ROUTES.LIST

    const handleSubscribe = () => {
        if (!item) {
            return
        }
        subscribeMutation.mutate({ catalog: Number(catalogId), level: "item", catalog_item: item.id })
    }

    const handleUnsubscribe = () => {
        if (!itemSubscription) {
            return
        }
        unsubscribeMutation.mutate(itemSubscription)
    }

    const renderSubscriptionAction = () => {
        if (!item) {
            return null
        }

        if (subscriptionsQuery.isLoading) {
            return <Skeleton height={32} width={110} />
        }

        if (!isSubscribed) {
            if (!item.is_active) {
                return (
                    <Hint
                        colorScheme="amber"
                        body="This item is no longer in the catalog, so it can't be subscribed to."
                        defaultPlacement="bottom"
                    >
                        <Button
                            variant="solid"
                            size="sm"
                            leftIcon={faIcon({ name: "circle-plus", style: "regular" })}
                            onClick={() => undefined}
                            isDisabled={true}
                        >
                            Subscribe
                        </Button>
                    </Hint>
                )
            }

            return (
                <Button
                    variant="solid"
                    size="sm"
                    leftIcon={faIcon({ name: "circle-plus", style: "regular" })}
                    onClick={handleSubscribe}
                    isProcessing={subscribeMutation.isPending}
                    isDisabled={subscribeMutation.isPending}
                >
                    Subscribe
                </Button>
            )
        }

        if (coveringSubscription?.level === "item") {
            return (
                <Button
                    variant="subtle"
                    size="sm"
                    onClick={handleUnsubscribe}
                    isProcessing={unsubscribeMutation.isPending}
                    isDisabled={unsubscribeMutation.isPending}
                >
                    Unsubscribe
                </Button>
            )
        }

        return null
    }

    const renderPricebookItemValue = () => {
        if (!item?.link.pricebook_item_id) {
            return "Not imported"
        }

        const pricebookItemLabel = item.link.pricebook_item_description ?? "View PriceBook item"
        const pricebookItemRoute = replaceSlugs(pricebookItemUpdateTemplate, { id: item.link.pricebook_item_id })

        return (
            <AccessCheck
                permissions={{ pricebook_view_permission: PERMISSION_LEVEL.FULL }}
                placeholder={pricebookItemLabel}
            >
                <LinkButton colorScheme="blue" onClick={() => window.location.assign(pricebookItemRoute)}>
                    {pricebookItemLabel}
                </LinkButton>
            </AccessCheck>
        )
    }

    const renderBody = () => {
        if (itemQuery.isLoading) {
            return (
                <div className={styles.skeleton}>
                    <Skeleton height={320} />
                </div>
            )
        }

        if (itemQuery.isError || !item) {
            return (
                <EmptyState
                    variant="error"
                    title="Couldn't load this catalog item"
                    supportText="Please refresh the page and try again."
                />
            )
        }

        const formattedWeight = formatMeasurement(item.weight)

        return (
            <div className={styles.layout}>
                <ItemGallery item={item} />
                <div className={styles.details}>
                    <div className={styles.header}>
                        <h1 className={styles.itemName}>{item.name}</h1>
                        {!subscriptionsQuery.isLoading && isSubscribed && (
                            <AlertBadge status="success" size="sm" showIcon={true} isLoading={false}>
                                Subscribed
                            </AlertBadge>
                        )}
                        {!item.is_active && (
                            <AlertBadge status="neutral" size="sm" showIcon={true} isLoading={false}>
                                No longer in catalog
                            </AlertBadge>
                        )}
                        {renderSubscriptionAction()}
                    </div>
                    <ItemPrices item={item} currencyCode={currencyCode} languageCode={languageCode} />

                    <div className={styles.attributes}>
                        <ValueDisplay variant="vertical" label="SKU" showLabel={true} value={item.sku || "—"} />
                        <ValueDisplay
                            variant="vertical"
                            label="Brand"
                            showLabel={true}
                            value={item.brand?.name ?? "—"}
                        />
                        <ValueDisplay
                            variant="vertical"
                            label="Categories"
                            showLabel={true}
                            value={formatCategoryNames(item.categories) || "—"}
                        />
                        <ValueDisplay
                            variant="vertical"
                            label="Weight"
                            showLabel={true}
                            value={formattedWeight ? `${formattedWeight} ${item.weight_unit_display}` : "—"}
                        />
                        <ValueDisplay
                            variant="vertical"
                            label="Dimensions"
                            showLabel={true}
                            value={formatDimensions(item) || "—"}
                        />
                        <ValueDisplay
                            variant="vertical"
                            label="Linked PriceBook Item"
                            showLabel={true}
                            value={renderPricebookItemValue()}
                        />
                    </div>

                    {item.description && (
                        <div className={styles.description}>
                            <h2 className={styles.sectionTitle}>Description</h2>
                            <p className={styles.descriptionBody}>{htmlToPlainText(item.description)}</p>
                        </div>
                    )}

                    <ItemOptions itemId={itemId} />
                </div>
            </div>
        )
    }

    return (
        <>
            <TopBar.Root>
                <TopBar.Header>
                    <TopBar.TopBarHeaderStart>
                        <TopBar.PageInnerTitle
                            label={item?.name ?? "Catalog Item"}
                            backDestination={backDestination}
                        />
                    </TopBar.TopBarHeaderStart>
                </TopBar.Header>
            </TopBar.Root>
            <AppFrame.PageContent>{renderBody()}</AppFrame.PageContent>
        </>
    )
}

export default function ItemDetails() {
    return (
        <AppFrame.Root navigationType="settings">
            <ItemDetailsContent />
        </AppFrame.Root>
    )
}
